// Wrapper that provides mock data with full inline rendering for demo mode

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, ChevronDown, Settings, LogOut, User, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { PropertyCard, PropertyCardData } from './PropertyCard';
import { FilterBar, FilterState } from './FilterBar';
import { HiveFund } from './HiveFundPortal';
import { CryptoStaking } from './CryptoStaking';
import { PortalDashboardContent } from './PortalDashboardContent';
import { AccountSettings } from '../../pages/AccountSettings';
import { MOCK_USER } from '../../data/mockData';
import { getTenantDemoData } from '../../data/demoDataResolver';
import { DEMO_ONBOARDING_DATA } from '../../data/mockOnboarding';
import { Property } from '../../components/Investment/PropertyCard';
import { UserProvider } from '../../contexts/UserContext';
import { BRAND_ASSETS, TENANT } from '../../config';
import { AccountSetupBanner } from '../Auth/AccountSetupBanner';
import type { OnboardingState } from '../Auth/AccountSetupBanner.types';

// Helper function to convert PropertyCardData to Property
const convertPropertyCardDataToProperty = (cardData: PropertyCardData): Property => {
  return {
    id: cardData.id,
    address: cardData.address,
    city: cardData.city,
    state: cardData.state,
    price: cardData.price,
    tokenSupply: cardData.totalShares,
    expectedRoi: cardData.targetReturn,
    status: cardData.status,
    imageUrl: cardData.imageUrl,
    propertyType: cardData.propertyType,
    investors: cardData.investorCount,
  };
};

// Helper function to convert Property to PropertyCardData
const convertToPropertyCardData = (property: Property): PropertyCardData => {
  return {
    id: property.id,
    name: property.address,
    address: property.address,
    city: property.city,
    state: property.state,
    imageUrl: property.imageUrl || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&auto=format&fit=crop&q=80',
    propertyType: property.propertyType || 'single-family',
    price: property.price,
    totalShares: property.tokenSupply,
    availableShares: property.tokenSupply - (property.investors || 0),
    targetReturn: property.expectedRoi,
    investorCount: property.investors || 0,
    fundedPercentage: Math.round(((property.investors || 0) / property.tokenSupply) * 100),
    status: property.status === 'available' ? 'available' : 'funded',
    tags: [],
  };
};

// Resolve tenant-specific demo data once at module level
const TENANT_DEMO = getTenantDemoData();

/**
 * Mock API Service for Demo
 * Simulates async API calls with realistic delays
 */
class DemoAPIService {
  private static SIMULATED_DELAY = 800;

  static async fetchProperties(): Promise<{ properties: Property[] }> {
    await this.delay(this.SIMULATED_DELAY);
    const properties = TENANT_DEMO.properties.map(convertPropertyCardDataToProperty);
    return { properties };
  }

  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * Custom hook for demo data management
 */
const useDemoData = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDemoData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await DemoAPIService.fetchProperties();
        setProperties(response.properties);
        setLoading(false);
      } catch (err) {
        console.error('Demo data loading error:', err);
        setError('Failed to load demo data');
        setLoading(false);
      }
    };

    loadDemoData();
  }, []);

  return { properties, loading, error };
};

/**
 * DemoPortal Component
 * Self-contained portal with inline rendering for demo mode
 */
export const DemoPortal: React.FC = () => {
  const navigate = useNavigate();
  const { properties, loading, error } = useDemoData();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'offerings' | 'funds' | 'crypto'>('offerings');
  const [activeSection, setActiveSection] = useState<string>('invest');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    propertyType: 'all',
    searchQuery: '',
    minPrice: 0,
    maxPrice: 10000000,
    location: '',
  });

  // Onboarding banner state
  const [onboardingData, setOnboardingData] = useState<OnboardingState | null>(DEMO_ONBOARDING_DATA);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  const handleExitDemo = () => {
    navigate('/');
  };

  // Filter function for PropertyCardData (works for both properties and funds)
  const applyCardFilters = (card: PropertyCardData): boolean => {
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const matchesSearch =
        card.name.toLowerCase().includes(query) ||
        card.address.toLowerCase().includes(query) ||
        card.city.toLowerCase().includes(query) ||
        card.state.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    if (card.price < filters.minPrice || card.price > filters.maxPrice) {
      return false;
    }

    if (filters.location) {
      const locationFilter = filters.location.toLowerCase();
      const matchesLocation =
        card.city.toLowerCase().includes(locationFilter) ||
        card.state.toLowerCase().includes(locationFilter);
      if (!matchesLocation) return false;
    }

    if (filters.propertyType !== 'all') {
      // Check if propertyType matches OR if tags include the filter
      const normalizedType = card.propertyType?.toLowerCase().replace(/\s+/g, '-');
      const matchesType = normalizedType === filters.propertyType;
      const matchesTags = card.tags?.includes(filters.propertyType) || false;
      if (!matchesType && !matchesTags) {
        return false;
      }
    }

    return true;
  };

  // Build card list from tenant-specific data (fund cards already included)
  const allCards: PropertyCardData[] = useMemo(() => {
    // Use tenant demo properties directly — they already include fund cards
    return TENANT_DEMO.properties;
  }, []);

  const filteredCards = allCards.filter(applyCardFilters);

  return (
    <UserProvider>
      <DemoDataProvider
        properties={properties}
        loading={loading}
        error={error}
      >
        <div className="min-h-screen bg-background portal-typography">
          {/* Demo Mode Banner */}
          <div className="bg-white border-b border-border py-3 px-4 text-center font-medium sticky top-0 z-[60] shadow-sm">
            <div className="container mx-auto flex items-center justify-between">
              <div className="flex-1" />
              <span className="text-sm md:text-base text-foreground">
                Demo Mode - Viewing Sample Data
              </span>
              <button
                onClick={handleExitDemo}
                className="text-sm text-foreground underline hover:no-underline ml-4 transition-opacity hover:opacity-70"
              >
                Exit Demo
              </button>
            </div>
          </div>

          {/* Header */}
          <header className="border-b border-border bg-[#F0F0F0] sticky top-[49px] z-50">
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-start gap-8">
                {/* Logo */}
                <div className="flex items-center flex-shrink-0">
                  <img
                    src={BRAND_ASSETS.logo.primary}
                    alt={`${TENANT.branding.name} Logo`}
                    className="h-12 w-auto cursor-pointer font-logo"
                    onClick={() => setActiveSection('invest')}
                  />
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden md:flex items-center gap-6">
                  <button
                    onClick={() => setActiveSection('invest')}
                    className={`py-2 text-base font-medium transition-all relative ${
                      activeSection === 'invest'
                        ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Invest
                  </button>
                  <button
                    onClick={() => setActiveSection('portfolio')}
                    className={`py-2 text-base font-medium transition-all relative ${
                      activeSection === 'portfolio'
                        ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Portfolio
                  </button>
                </nav>

                {/* User dropdown - Desktop */}
                <div className="hidden md:flex items-center gap-2 relative ml-auto">
                  <Button
                    variant="ghost"
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    <span>Demo User</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>

                  {userDropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-lg shadow-lg py-1 z-50">
                      <button
                        onClick={() => {
                          setActiveSection('profile');
                          setUserDropdownOpen(false);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </button>
                      <button
                        onClick={() => {
                          setUserDropdownOpen(false);
                          handleExitDemo();
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm"
                      >
                        <LogOut className="w-4 h-4" />
                        Exit Demo
                      </button>
                    </div>
                  )}
                </div>

                {/* Mobile Menu */}
                <div className="flex items-center gap-2 md:hidden ml-auto">
                  <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                    <SheetTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <Menu className="w-5 h-5" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                      <nav className="flex flex-col gap-6 mt-8">
                        <button
                          onClick={() => { setActiveSection('invest'); setIsMenuOpen(false); }}
                          className="text-lg font-medium text-foreground hover:text-primary transition-colors text-left"
                        >
                          Invest
                        </button>
                        <button
                          onClick={() => { setActiveSection('portfolio'); setIsMenuOpen(false); }}
                          className="text-lg font-medium text-foreground hover:text-primary transition-colors text-left"
                        >
                          Portfolio
                        </button>
                        <hr className="border-border" />
                        <button
                          onClick={() => { setActiveSection('profile'); setIsMenuOpen(false); }}
                          className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-primary transition-colors text-left"
                        >
                          <Settings className="w-5 h-5" />
                          Settings
                        </button>
                        <button
                          onClick={() => { handleExitDemo(); setIsMenuOpen(false); }}
                          className="flex items-center gap-2 text-lg font-medium text-foreground hover:text-primary transition-colors text-left"
                        >
                          <LogOut className="w-5 h-5" />
                          Exit Demo
                        </button>
                      </nav>
                    </SheetContent>
                  </Sheet>
                </div>
              </div>
            </div>
          </header>

          {/* Onboarding Banner - appears below nav, above content */}
          {!onboardingDismissed && (
            <div className="container mx-auto px-4 mt-6">
              <AccountSetupBanner
                onboarding={onboardingData}
                error={null}
                onStepClick={(stepId) => {
                  console.log(`Demo: Clicked step ${stepId}`);
                  // In demo mode, just log the click
                  // Could navigate to settings if implemented
                }}
                onRetry={() => {
                  console.log('Demo: Retry clicked');
                  setOnboardingData(DEMO_ONBOARDING_DATA);
                }}
              />
            </div>
          )}

          {/* Content based on activeSection */}
          {activeSection === 'portfolio' && (
            <PortalDashboardContent />
          )}

          {activeSection === 'profile' && (
            <AccountSettings />
          )}

          {activeSection === 'invest' && (
            <div className="container mx-auto px-4 py-6 md:py-8">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'offerings' | 'funds' | 'crypto')} className="mb-6">
                <TabsList className="grid w-full max-w-md grid-cols-3">
                  <TabsTrigger value="offerings">{TENANT.branding.propertyLabel}</TabsTrigger>
                  <TabsTrigger value="funds">{TENANT_DEMO.fundDisplayName}</TabsTrigger>
                  <TabsTrigger value="crypto">Crypto</TabsTrigger>
                </TabsList>

                {/* Offerings Tab Content */}
                <TabsContent value="offerings" className="mt-6">
                  <FilterBar
                    filters={filters}
                    onFilterChange={setFilters}
                    propertyCount={filteredCards.length}
                    categories={TENANT_DEMO.investCategories}
                  />

                  <div className="mt-6">
                    {loading ? (
                      <div className="flex items-center justify-center py-20">
                        <Loader2 className="animate-spin text-primary" size={40} />
                      </div>
                    ) : error ? (
                      <div className="flex flex-col items-center justify-center py-20 space-y-4">
                        <AlertCircle className="text-destructive" size={48} />
                        <p className="text-muted-foreground text-center">{error}</p>
                      </div>
                    ) : filteredCards.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-20 space-y-4">
                        <div className="w-16 h-16 bg-card rounded-full flex items-center justify-center border border-border">
                          <AlertCircle className="text-muted-foreground" size={32} />
                        </div>
                        <p className="text-muted-foreground text-center">
                          No offerings match your filters
                        </p>
                        {(filters.searchQuery || filters.minPrice > 0 || filters.propertyType !== 'all') && (
                          <Button
                            onClick={() => setFilters({
                              propertyType: 'all',
                              searchQuery: '',
                              minPrice: 0,
                              maxPrice: 10000000,
                              location: '',
                            })}
                            variant="outline"
                          >
                            Clear Filters
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCards.map((card) => (
                          <PropertyCard
                            key={card.id}
                            property={card}
                            onClick={() => {}}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Funds Tab Content */}
                <TabsContent value="funds" className="mt-6">
                  <HiveFund
                    fundName={TENANT_DEMO.fundDisplayName}
                    description={TENANT_DEMO.fundDescription}
                    metrics={TENANT_DEMO.fundMetrics}
                    fundraising={TENANT_DEMO.fundFundraising}
                    performanceData={TENANT_DEMO.fundPerformance}
                    assetAllocation={TENANT_DEMO.fundAssetAllocation}
                    geographicDistribution={TENANT_DEMO.fundGeoDistribution}
                    highlights={TENANT_DEMO.fundHighlights}
                    disclaimer={TENANT_DEMO.fundDisclaimer}
                    investButtonText={`Invest in ${TENANT_DEMO.fundDisplayName}`}
                  />
                </TabsContent>

                {/* Crypto Tab Content */}
                <TabsContent value="crypto" className="mt-6">
                  <CryptoStaking />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </DemoDataProvider>
    </UserProvider>
  );
};

/**
 * Demo Data Context Provider
 */
interface DemoDataContextValue {
  properties: Property[];
  loading: boolean;
  error: string | null;
  user: typeof MOCK_USER;
  portfolioHoldings: any[];
  dashboardSummary: any;
  cryptoAssets: any[];
  cryptoNetworkFee: { amount: number; isEstimate: boolean };
  hiveFundMetrics: any;
  hiveFundFundraising: any;
  hiveFundPerformance: any[];
  hiveFundAssetAllocation: any[];
  hiveFundGeoDistribution: any[];
  hiveFundHighlights: any[];
  hiveFundDescription: string;
  hiveFundDisclaimer: string;
  isDemo: boolean;
}

const DemoDataContext = React.createContext<DemoDataContextValue | null>(null);

interface DemoDataProviderProps {
  properties: Property[];
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
}

const DemoDataProvider: React.FC<DemoDataProviderProps> = ({
  properties,
  loading,
  error,
  children,
}) => {
  const value: DemoDataContextValue = {
    properties,
    loading,
    error,
    user: MOCK_USER,
    portfolioHoldings: TENANT_DEMO.portfolioHoldings,
    dashboardSummary: TENANT_DEMO.dashboardSummary,
    cryptoAssets: TENANT_DEMO.cryptoAssets,
    cryptoNetworkFee: TENANT_DEMO.cryptoNetworkFee,
    hiveFundMetrics: TENANT_DEMO.fundMetrics,
    hiveFundFundraising: TENANT_DEMO.fundFundraising,
    hiveFundPerformance: TENANT_DEMO.fundPerformance,
    hiveFundAssetAllocation: TENANT_DEMO.fundAssetAllocation,
    hiveFundGeoDistribution: TENANT_DEMO.fundGeoDistribution,
    hiveFundHighlights: TENANT_DEMO.fundHighlights,
    hiveFundDescription: TENANT_DEMO.fundDescription,
    hiveFundDisclaimer: TENANT_DEMO.fundDisclaimer,
    isDemo: true,
  };

  return (
    <DemoDataContext.Provider value={value}>
      {children}
    </DemoDataContext.Provider>
  );
};

/**
 * Hook to access demo data context
 */
export const useDemoDataContext = () => {
  const context = React.useContext(DemoDataContext);
  return context;
};
